import React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useOportunidadeModal } from '../context/OportunidadeModalContext'
import type { OportunidadeModalItem } from '../context/OportunidadeModalContext'
import OportunidadeModalRight from './OportunidadeModalRight'
import { Button } from '@/shared/ui'
import { ArrowLeft, X, LineChart } from 'lucide-react'
import { OHLCVChart } from '@/modules/graph-view'
import type { TF } from '@/modules/graph-view/types'
import RSIPane from '@/modules/graph-view/components/RSIPane'
import { useCandles } from '@/modules/graph-view/hooks/useCandles'
import { normalizeCandles, filterOutliers } from '@/modules/graph-view/utils/normalize'
import { useOportunidadeDetails } from '../hooks/useOportunidadeDetails'
import { deriveFiboForOpportunity } from '../utils/fibo'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { useUserTimezone } from '@/modules/auth/hooks/useUserTimezone'
import { getOverlaysForMembers } from '@/modules/settings/services/chartOverlaySettingsService'
import { useSignalsEnabled } from '@/modules/signals/hooks/useSignalsEnabled'
import { useFiboTargetsEnabled } from '@/modules/signals/hooks/useFiboTargetsEnabled'
import { useOpportunitySignals } from '@/modules/signals/hooks/useOpportunitySignals'
import { useSignalsOverlayLevels } from '@/modules/signals/hooks/useSignalsOverlayLevels'
import OpportunitySignalsOverlay from './OpportunitySignalsOverlay'
import OpportunityFiboTargetsOverlay from './OpportunityFiboTargetsOverlay'

function toPair(symbol?: string | null): string {
  const s = String(symbol || '').trim().toUpperCase()
  const m = s.match(/^(.*?)(USDT|USDC|BTC)$/)
  if (m) return `${m[1]}/${m[2]}`
  const clean = s.replace(/[^A-Z0-9]/g, '')
  const m2 = clean.match(/^(.*?)(USDT|USDC|BTC)$/)
  if (m2) return `${m2[1]}/${m2[2]}`
  return s
}

const TF_OPTIONS: TF[] = ['5','15','30','60','240','720','D','W','M']
function tfLabelLocal(tf: TF): string {
  switch (tf) {
    case '5': return '5m'
    case '15': return '15m'
    case '30': return '30m'
    case '60': return '1h'
    case '240': return '4h'
    case '720': return '12h'
    case 'D': return '1D'
    case 'W': return '1W'
    case 'M': return '1M'
    default: return String(tf)
  }
}
export default function MobileOpportunitySheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { item } = useOportunidadeModal()
  const { data, asset } = useOportunidadeDetails(item)
  const [mode, setMode] = React.useState<'info'|'chart'>('info')
  const [tf, setTf] = React.useState<TF>('60')
  const [ema12, setEma12] = React.useState(true)
  const [ema26, setEma26] = React.useState(true)
  const [ema200, setEma200] = React.useState(true)
  const [showFibo, setShowFibo] = React.useState(false)
  const [showTechV2, setShowTechV2] = React.useState(false)
  const [allowed, setAllowed] = React.useState({ ema12: true, ema26: true, ema200: true, fibo: true })
  const { profile } = useAuth(); const isMaster = profile?.role === 'master'
  const { timezone } = useUserTimezone()
  const signalsEnabled = useSignalsEnabled()
  const fiboTargetsEnabled = useFiboTargetsEnabled()
  const { signals } = useOpportunitySignals(item?.id ?? null, isMaster || !!signalsEnabled)
  const signalsOverlayLevels = useSignalsOverlayLevels(isMaster || !!signalsEnabled)
  const [showSignalsOverlay, setShowSignalsOverlay] = React.useState(false)

  React.useEffect(() => {
    if (!isMaster && !fiboTargetsEnabled) setShowTechV2(false)
  }, [isMaster, fiboTargetsEnabled])

  // Quando o modal for aberto a partir do fluxo "Estou dentro" com for��a de gr��fico,
  // inicializa o modo e o timeframe com base nos hints do item.
  React.useEffect(() => {
    if (!open) return
    const typed = (item || null) as OportunidadeModalItem | null

    if (typed?.chartForceSignalsOverlay) {
      setMode('chart')
      if (isMaster || !!signalsEnabled) {
        setShowSignalsOverlay(true)
      }
    } else {
      setMode('info')
    }

    if (typed?.chartInitialTimeframe) {
      let raw = String(typed.chartInitialTimeframe || '').toUpperCase()
      if (raw === '1D') raw = 'D'
      if (raw === '1W') raw = 'W'
      if (raw === '1M') raw = 'M'
      const allowed: TF[] = ['5','15','30','60','240','720','D','W','M']
      if (allowed.includes(raw as TF)) {
        setTf(raw as TF)
      }
    }
  }, [open, item, isMaster, signalsEnabled])
  React.useEffect(() => {
    let mounted = true
    getOverlaysForMembers().then((cfg)=>{
      if (!mounted) return
      const next = isMaster ? { ema12: true, ema26: true, ema200: true, fibo: true } : cfg
      setAllowed(next)
      if (!next.ema12) setEma12(false)
      if (!next.ema26) setEma26(false)
      if (!next.ema200) setEma200(false)
      if (!next.fibo) setShowFibo(false)
    }).catch(()=>{})
    return () => { mounted = false }
  }, [profile?.role])
  const [tfOpen, setTfOpen] = React.useState(false)
  const [optsOpen, setOptsOpen] = React.useState(false)
  const chartRef = React.useRef<any>(null)
  const [chartReadyTick, setChartReadyTick] = React.useState(0)
  const handleReady = React.useCallback((c:any)=>{ chartRef.current = c; try { setChartReadyTick((x)=>x+1) } catch {} }, [])
  const fiboSeriesRef = React.useRef<any[]>([])

  // RSI (mesma configuração da versão desktop)
  // Usar símbolo do detalhe quando o item inicial não o tiver (deep link via push)
  const symbol = String(((data as any)?.symbol || item?.symbol || '')).toUpperCase()
  // Força refetch quando o usuário abre o modo gráfico pela primeira vez (deep link via push)
  const [refreshKey, setRefreshKey] = React.useState(0)
  React.useEffect(() => { if (mode === 'chart' && symbol) setRefreshKey((x)=> x + 1) }, [mode, symbol, tf])
  const { data: rsiCandles } = useCandles(symbol, tf, 500, refreshKey)
  const rsiBucketed = React.useMemo(() => normalizeCandles((rsiCandles as any) || [], tf), [rsiCandles, tf])
  const rsiFiltered = React.useMemo(() => filterOutliers(rsiBucketed as any, tf), [rsiBucketed, tf])

  // Fibo overlay (mesma lógica do desktop)
  const [fiboOverlay, setFiboOverlay] = React.useState<{ hi?: number|null; lo?: number|null; f382?: number|null; f50?: number|null; f618?: number|null } | null>(null)
  React.useEffect(() => {
    let cancel=false
    deriveFiboForOpportunity(data as any, { limit: 300, k: 2 }).then((res)=>{ if(!cancel) setFiboOverlay(res||null) }).catch(()=>{ if(!cancel) setFiboOverlay(null)})
    return () => { cancel = true }
  }, [data?.id])
  React.useEffect(() => {
    const chart = chartRef.current
    try {
      for (const s of fiboSeriesRef.current) {
        try { s._pl && s.removePriceLine && s.removePriceLine(s._pl) } catch {}
        try { chart && s && chart.removeSeries ? chart.removeSeries(s) : s.remove() } catch {}
      }
    } catch {}
    fiboSeriesRef.current = []
    if (!chart || !showFibo || !allowed.fibo || !fiboOverlay) return
    const levels: Array<{ key: string; value: number; color?: string }> = []
    const trendRaw = String((data as any)?.target_trend || ((data as any)?.confluence_factors||{}).trend_alvo || '').toLowerCase()
    const isShort = (trendRaw === 'bearish' || trendRaw === 'baixa' || trendRaw === 'short')
    const hiV = Number(fiboOverlay.hi as any)
    const loV = Number(fiboOverlay.lo as any)
    const f382 = Number(fiboOverlay.f382 as any)
    const f50  = Number(fiboOverlay.f50 as any)
    const f618 = Number(fiboOverlay.f618 as any)
    if (!isShort) {
      if (Number.isFinite(hiV)) levels.push({ key: '0', value: hiV, color: 'rgba(148,163,184,0.9)' })
      if (Number.isFinite(f382)) levels.push({ key: '0.382', value: f382, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(f50)) levels.push({ key: '0.5', value: f50, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(f618)) levels.push({ key: '0.618', value: f618, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(loV)) levels.push({ key: '1', value: loV, color: 'rgba(148,163,184,0.9)' })
    } else {
      if (Number.isFinite(loV)) levels.push({ key: '0', value: loV, color: 'rgba(148,163,184,0.9)' })
      if (Number.isFinite(f618)) levels.push({ key: '0.382', value: f618, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(f50)) levels.push({ key: '0.5', value: f50, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(f382)) levels.push({ key: '0.618', value: f382, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(hiV)) levels.push({ key: '1', value: hiV, color: 'rgba(148,163,184,0.9)' })
    }
    const firstT = Number((rsiFiltered as any[])?.[0]?.time)
    const lastT = Number((rsiFiltered as any[])?.[(rsiFiltered as any[])?.length - 1]?.time)
    if (!Number.isFinite(firstT) || !Number.isFinite(lastT) || firstT >= lastT) return
    for (const lv of levels) {
      try {
        const s = chart.addLineSeries({ color: lv.color || 'rgba(156,163,175,0.9)', lineWidth: 1, lineStyle: 2, lastValueVisible: false, priceLineVisible: false } as any)
        // usa times arbitrários alinhados à escala lógica atual (serão estendidos automaticamente)
        s.setData([{ time: firstT as any, value: lv.value }, { time: lastT as any, value: lv.value }])
        const pl = s.createPriceLine({ price: lv.value, color: lv.color || 'rgba(156,163,175,0.9)', lineStyle: 2, lineWidth: 1, axisLabelVisible: true, title: `${lv.key}` } as any)
        ;(s as any)._pl = pl
        fiboSeriesRef.current.push(s)
      } catch {}
    }
  }, [showFibo, allowed.fibo, fiboOverlay, tf, rsiFiltered, chartReadyTick])

  if (!open) return null

  const titleName = (asset as any)?.name || (data as any)?.symbol || item?.name || item?.symbol || ''
  const titlePair = toPair((data as any)?.symbol || item?.symbol)

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="lg:hidden fixed inset-0 z-[90]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/40" onClick={onClose} />
          <motion.div
            className="absolute inset-x-0 bottom-0 top-0 bg-background"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            role="dialog"
            aria-modal="true"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex min-w-0 items-center gap-2">
                {((asset as any)?.logo_url || item?.logo_url) ? (
                  <img src={((asset as any)?.logo_url || item?.logo_url) as string} alt={titleName} className="h-6 w-6 rounded-full object-cover" />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-muted" />
                )}
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{titleName}</div>
                  <div className="truncate text-xs text-foreground/70">{titlePair}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {mode === 'chart' && (
                  <>
                    <button
                      type="button"
                      onClick={() => { setTfOpen(v=>!v); setOptsOpen(false) }}
                      className="inline-flex h-8 items-center rounded-md border border-border/60 bg-background/60 px-2 text-sm shadow hover:bg-muted"
                      aria-label="Selecionar timeframe"
                    >
                      {tfLabelLocal(tf)}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setOptsOpen(v=>!v); setTfOpen(false) }}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border/60 bg-background/60 p-0 shadow hover:bg-muted"
                      aria-label="Opções de overlays"
                    >
                      <LineChart className="h-4 w-4" />
                    </button>
                  </>
                )}
                {mode === 'chart' ? (
                  <button onClick={() => setMode('info')} className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm hover:bg-muted">
                    <ArrowLeft className="h-4 w-4" /> Voltar
                  </button>
                ) : (
                  <button aria-label="Fechar" onClick={onClose} className="rounded p-1 hover:bg-muted">
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            {mode === 'info' ? (
              <div className="flex h-[calc(100dvh-48px)] flex-col">
                <div className="flex-1 overflow-y-auto px-4 py-4 pb-28">
                  {/* Reutiliza o conteúdo da versão desktop sem cabeçalho duplicado */}
                  <OportunidadeModalRight key={item?.id || 'none'} showHeader={false} />
                  <Button variant="outline" className="mt-4 w-full" onClick={() => setMode('chart')}>
                    <LineChart className="mr-2 h-4 w-4" /> Abrir gráfico
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex h-[calc(100dvh-48px)] flex-col">
                {/* Chart body fills remaining height */}
                <div className="relative flex-1 min-h-0 overflow-hidden pb-28">
                  <OHLCVChart
                    symbol={symbol}
                    timeframe={tf}
                    className="h-full w-full"
                    timezone={timezone}
                    showInternalControls={false}
                    ema12={ema12}
                    ema26={ema26}
                    ema200={ema200}
                    onReady={handleReady}
                    footerHeight={140}
                    footerSlot={(
                      <RSIPane
                        className="h-full w-full relative"
                        candles={rsiFiltered as any}
                        timeframe={tf}
                        syncTo={chartRef.current}
                        // inclui chartReadyTick para forçar re-subscribe quando o chart fica pronto
                        syncLen={rsiFiltered.length + chartReadyTick}
                      />
                      )}
                    />
                    <OpportunityFiboTargetsOverlay
                      chartRef={chartRef}
                      candles={rsiFiltered as any}
                      timeframe={tf}
                      opportunity={data}
                      fibo={fiboOverlay as any}
                      enabled={showTechV2 && !!(isMaster || !!fiboTargetsEnabled)}
                    />
                    <OpportunitySignalsOverlay
                      chartRef={chartRef}
                      candles={rsiFiltered as any}
                      timeframe={tf}
                      opportunity={data}
                      signals={signals}
                      overlayLevels={signalsOverlayLevels}
                      enabled={!!(isMaster || !!signalsEnabled) && showSignalsOverlay}
                    />
                  </div>

                {/* Timeframe panel (top floating) */}
                <AnimatePresence>
                  {tfOpen && (
                    <motion.div
                      initial={{ y: -12, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -12, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="fixed inset-x-0 top-[56px] z-[60] px-2"
                    >
                      <div className="mx-auto w-[min(100vw-16px,100%)] max-w-none rounded-lg border border-border bg-background/95 px-2 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                        <div className="no-scrollbar flex gap-2 overflow-x-auto px-1 py-1">
                          {TF_OPTIONS.map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => { setTf(opt); setTfOpen(false) }}
                              className={"rounded-md px-3 py-2 text-sm border whitespace-nowrap " + (tf===opt ? 'border-primary text-primary' : 'border-border text-foreground/80 hover:bg-muted')}
                            >
                              {tfLabelLocal(opt)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Overlays panel (top floating) */}
                <AnimatePresence>
                  {optsOpen && (
                    <motion.div
                      initial={{ y: -12, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -12, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="fixed inset-x-0 top-[56px] z-[60] px-2"
                    >
                      <div className="mx-auto w-[min(100vw-16px,100%)] max-w-none rounded-lg border border-border bg-background/95 px-3 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                        <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
                          {allowed.ema12 && (
                            <label className="inline-flex items-center gap-1 select-none">
                              <input type="checkbox" checked={ema12} onChange={(e)=> setEma12(e.target.checked)} />
                              <span className="text-foreground/80" style={{ color: '#FFD600' }}>EMA 12</span>
                            </label>
                          )}
                          {allowed.ema26 && (
                            <label className="inline-flex items-center gap-1 select-none">
                              <input type="checkbox" checked={ema26} onChange={(e)=> setEma26(e.target.checked)} />
                              <span className="text-foreground/80" style={{ color: '#00BCD4' }}>EMA 26</span>
                            </label>
                          )}
                          {allowed.ema200 && (
                            <label className="inline-flex items-center gap-1 select-none">
                              <input type="checkbox" checked={ema200} onChange={(e)=> setEma200(e.target.checked)} />
                              <span className="text-foreground/80" style={{ color: '#8E44AD' }}>EMA 200</span>
                            </label>
                          )}
                          {allowed.fibo && (
                            <label className="inline-flex items-center gap-1 select-none">
                              <input type="checkbox" checked={showFibo} onChange={(e)=> setShowFibo(e.target.checked)} />
                              <span className="text-foreground/80">Fibo (alvo)</span>
                            </label>
                          )}
                          {(isMaster || !!fiboTargetsEnabled) && (
                            <label className="inline-flex items-center gap-1 select-none">
                              <input type="checkbox" checked={showTechV2} onChange={(e)=> setShowTechV2(e.target.checked)} />
                              <span className="text-foreground/80">Dados Técnicos V2</span>
                            </label>
                          )}
                          {(isMaster || !!signalsEnabled) && (
                            <label className="inline-flex items-center gap-1 select-none">
                              <input
                                type="checkbox"
                                checked={showSignalsOverlay}
                                onChange={(e) => setShowSignalsOverlay(e.target.checked)}
                              />
                              <span className="text-foreground/80" style={{ color: '#FBBF24' }}>Dados Técnicos</span>
                            </label>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Bottom controls (estático, acima do bottom nav) */}
                {/* moved to top: disabled old panel */}
                {false && (
                <div className="pointer-events-auto fixed left-0 right-0 bottom-[calc(env(safe-area-inset-bottom)+64px)] z-50 px-4">
                  <div className="mx-auto max-w-[640px] rounded-lg border border-border bg-background/95 px-2 pt-2 pb-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                  <div className="grid grid-cols-2 items-center gap-2">
                    <div className="min-w-0">
                      <button type="button" onClick={() => setTfOpen(v=>!v)} className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted">
                        Timeframe: {tfLabelLocal(tf)}
                      </button>
                    </div>
                    <div className="flex items-center justify-end gap-3 text-xs">
                      {allowed.ema12 && (
                        <label className="inline-flex items-center gap-1">
                          <input type="checkbox" checked={ema12} onChange={(e)=> setEma12(e.target.checked)} />
                          <span className="text-foreground/80" style={{ color: '#FFD600' }}>EMA 12</span>
                        </label>
                      )}
                      {allowed.ema26 && (
                        <label className="inline-flex items-center gap-1">
                          <input type="checkbox" checked={ema26} onChange={(e)=> setEma26(e.target.checked)} />
                          <span className="text-foreground/80" style={{ color: '#00BCD4' }}>EMA 26</span>
                        </label>
                      )}
                      {allowed.ema200 && (
                        <label className="inline-flex items-center gap-1">
                          <input type="checkbox" checked={ema200} onChange={(e)=> setEma200(e.target.checked)} />
                          <span className="text-foreground/80" style={{ color: '#8E44AD' }}>EMA 200</span>
                        </label>
                      )}
                      {(allowed.fibo || isMaster || !!fiboTargetsEnabled) && (
                        <label className="inline-flex items-center gap-1">
                          <input type="checkbox" checked={showTechV2} onChange={(e)=> setShowTechV2(e.target.checked)} />
                          <span className="text-foreground/80">Dados Técnicos V2</span>
                        </label>
                      )}
                    </div>
                  </div>
                  </div>
                </div>
                )}
                {/* end old panel */}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
